import React, { useState, useCallback, useMemo } from 'react';
import styled from '@emotion/styled';
import { Input } from 'reactstrap';
import { ActivityLog } from '../../../Model/Activity/ActivityLog';
import { Activity } from '../../../Model/Activity/Activity';
import { BaseTrackingKey, TrackingKeys } from '../../../Model/ProgramPortfolio/TrackingKeys';
import { Theme } from '../../../Theme/Theme';
import { useTrans } from '../../../Store/System/LocalizationStore';
import {
  useTrackingKeyLogic,
  getTrackingKeyIcon,
  ValidationResult,
  SuggestedValue,
} from './useTrackingKeyLogic';

const Row = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 16px 0;
  border-bottom: 1px solid ${Theme.Colors.lighterGrey};

  &:last-child {
    border-bottom: none;
  }
`;

const IconWrapper = styled.div<{ $color: string; $bgColor: string }>`
  width: 40px;
  height: 40px;
  border-radius: 10px;
  background: ${(props) => props.$bgColor};
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;

  i {
    font-size: 18px;
    color: ${(props) => props.$color};
  }
`;

const Content = styled.div`
  flex: 1;
  min-width: 0;
`;

const LabelRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 8px;
`;

const Label = styled.div`
  font-size: 15px;
  font-weight: 500;
  color: ${Theme.Colors.textColor};
`;

const SuggestedBadge = styled.span`
  font-size: 11px;
  font-weight: 600;
  color: ${Theme.Colors.primary};
  background: ${Theme.Colors.primary}15;
  padding: 2px 8px;
  border-radius: 10px;
`;

const InputRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const StyledInput = styled(Input)<{ $hasWarning?: boolean; $hasError?: boolean }>`
  flex: 1;
  max-width: 140px;
  text-align: right;
  padding: 10px 14px;
  border-radius: 10px;
  font-size: 18px;
  font-weight: 600;
  border: 2px solid ${({ $hasError, $hasWarning }) =>
    $hasError ? '#f44336' : $hasWarning ? '#ff9800' : Theme.Colors.lighterGrey};
  background: ${({ $hasError, $hasWarning }) =>
    $hasError ? '#fff5f5' : $hasWarning ? '#fffbf5' : Theme.Colors.lightestGrey};
  transition: all 0.2s ease;

  &:focus {
    border-color: ${({ $hasError, $hasWarning }) =>
      $hasError ? '#f44336' : $hasWarning ? '#ff9800' : Theme.Colors.primary};
    box-shadow: 0 0 0 3px ${({ $hasError, $hasWarning }) =>
      $hasError ? '#f4433620' : $hasWarning ? '#ff980020' : Theme.Colors.primary + '20'};
    background: white;
  }

  &::placeholder {
    color: ${Theme.Colors.warmGrey};
    font-weight: 400;
    font-size: 14px;
  }
`;

const Unit = styled.span`
  font-size: 14px;
  color: ${Theme.Colors.warmGrey};
  min-width: 50px;
`;

const MessageRow = styled.div<{ $type: 'error' | 'warning' | 'info' }>`
  display: flex;
  align-items: center;
  gap: 6px;
  margin-top: 6px;
  font-size: 12px;
  color: ${({ $type }) =>
    $type === 'error' ? '#f44336' : $type === 'warning' ? '#ff9800' : Theme.Colors.charcoal};

  i {
    font-size: 14px;
  }
`;

export type SmartTrackingKeyInputProps = {
  trackingKey: BaseTrackingKey;
  activityLog: ActivityLog;
  activity: Activity;
  bmr?: number;
  onDataPointChange: (key: BaseTrackingKey, value: number) => void;
};

export const SmartTrackingKeyInput: React.FC<SmartTrackingKeyInputProps> = ({
  trackingKey,
  activityLog,
  activity,
  bmr,
  onDataPointChange,
}) => {
  const { t } = useTrans();
  const [tempValue, setTempValue] = useState('');
  const [validation, setValidation] = useState<ValidationResult>({ isValid: true, isWarning: false });
  const [userHasTyped, setUserHasTyped] = useState(false);

  const { validateValue, getSuggestedValue, isAutoCalculated } = useTrackingKeyLogic({
    activityLog,
    activity,
    bmr,
  });

  const tkDef = TrackingKeys[trackingKey];
  const currentValue = activityLog.data[trackingKey] ?? 0;
  const isSuggested = isAutoCalculated(trackingKey);
  const suggested = getSuggestedValue(trackingKey);

  const updateDataPoint = useCallback(
    (key: BaseTrackingKey, value: number) => {
      onDataPointChange(key, value);
    },
    [onDataPointChange],
  );

  const inputValue = useMemo(() => {
    if (userHasTyped) {
      return tempValue;
    }
    if (isSuggested) {
      return '';
    }
    return currentValue > 0 ? String(currentValue) : '';
  }, [currentValue, isSuggested, tempValue, userHasTyped]);

  const handleFocus = useCallback(() => {
    if (isSuggested && !userHasTyped && suggested) {
      setTempValue(String(suggested.value));
      updateDataPoint(trackingKey, suggested.value);
      setUserHasTyped(true);
    }
  }, [isSuggested, userHasTyped, suggested, trackingKey, updateDataPoint]);

  const handleBlur = useCallback(() => {
    if (isSuggested && tempValue === '') {
      setUserHasTyped(false);
    }
  }, [isSuggested, tempValue]);

  const handleChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      let newValue = e.target.value;

      // Sanitize: allow only valid numbers
      newValue = newValue.replace(',', '.');
      if (newValue !== '' && !/^-?\d*\.?\d*$/.test(newValue)) {
        return;
      }

      if (!userHasTyped) {
        setUserHasTyped(true);
      }

      setTempValue(newValue);

      if (newValue.trim() === '' || newValue.trim() === '.') {
        setValidation({ isValid: true, isWarning: false });
        updateDataPoint(trackingKey, 0);
        return;
      }

      const numValue = parseFloat(newValue) || 0;
      const result = validateValue(trackingKey, numValue);
      setValidation(result);

      if (result.isValid) {
        updateDataPoint(trackingKey, numValue);
      }
    },
    [trackingKey, userHasTyped, validateValue, updateDataPoint],
  );

  if (!tkDef) return null;

  const iconClass = getTrackingKeyIcon(trackingKey);
  const placeholder = isSuggested && !userHasTyped && suggested ? suggested.label : '0';

  return (
    <Row>
      <IconWrapper $color={tkDef.iconColor} $bgColor={tkDef.iconContainerColor}>
        <i className={`bi ${iconClass}`} />
      </IconWrapper>
      <Content>
        <LabelRow>
          <Label>{t(`trackingKeys.${trackingKey}`, { defaultValue: trackingKey })}</Label>
          {isSuggested && suggested && !userHasTyped && (
            <SuggestedBadge>{t('activity.autoCalculated', { defaultValue: 'Auto' })}</SuggestedBadge>
          )}
        </LabelRow>
        <InputRow>
          <StyledInput
            type="text"
            inputMode="decimal"
          value={inputValue}
            placeholder={placeholder}
            onChange={handleChange}
            onFocus={handleFocus}
            onBlur={handleBlur}
            $hasError={!validation.isValid}
            $hasWarning={validation.isWarning}
          />
          <Unit>{tkDef.defaultUnit}</Unit>
        </InputRow>
        {validation.message && (
          <MessageRow $type={!validation.isValid ? 'error' : 'warning'}>
            <i className={`bi ${!validation.isValid ? 'bi-exclamation-circle' : 'bi-info-circle'}`} />
            {validation.message}
          </MessageRow>
        )}
      </Content>
    </Row>
  );
};
